import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Checkbox, Tag, Input, Tooltip, Icon, Button, Select } from "antd";
import GLCodeSelect from "../../common/SelectDropdowns/GLCodeSelect";
import I18n from "../../../common/I18n";

const NO_AUTO_CALC_SECTIONS = ["transportation_charges", "fuel_surcharges"];
const DRIVER_PAY_SECTIONS = ["adjustment_charges"];

const INVOICE_TYPE_MAP = {
  transportation_charges: "Transportation",
  fuel_surcharges: "Fuel Surcharges",
  adjustment_charges: "Adjustments",
  accessorial_charges: "Accessorial",
};

const REQUIRED_FIELD_MESSAGES = {
  invoice_name: "Can't be empty",
  location_id: "Can't be empty",
  gl_code: "Can't be empty",
  invoice_quantity: "Min value 1",
  invoice_value: "Can't be empty",
};

const newLineTemplate = (sectionKey, order) => ({
  invoice_name: "",
  invoice_value: 0,
  invoice_quantity: 1,
  isNew: true,
  invoice_type: INVOICE_TYPE_MAP[sectionKey] || "",
  order_item_group_id: null,
  location_id: (order?.all_locations && order.all_locations[0]?.location_id) || "",
  // location_type: (order && order.location_type) || "",
  order_id: (order && order.order_id) || "",
  accessorial_code: "",
  gl_code: "",
  is_modified: false,
  apply_driver_pay_for_adjustment: false,
});

// Returns an object of {field: message} for a single line's invalid fields
const getLineErrors = line => {
  const errors = {};

  if (!String(line.invoice_name || "").trim()) {
    errors.invoice_name = REQUIRED_FIELD_MESSAGES.invoice_name;
  }
  if (!line.location_id) {
    errors.location_id = REQUIRED_FIELD_MESSAGES.location_id;
  }
  if (!String(line.gl_code || "").trim()) {
    errors.gl_code = REQUIRED_FIELD_MESSAGES.gl_code;
  }

  const qty = line.invoice_quantity;
  if (qty === "" || qty === null || qty === undefined || Number(qty) < 1) {
    errors.invoice_quantity = REQUIRED_FIELD_MESSAGES.invoice_quantity;
  }

  const val = line.invoice_value;
  if (val === "" || val === null || val === undefined) {
    errors.invoice_value = REQUIRED_FIELD_MESSAGES.invoice_value;
  }

  return errors;
};

const hasIncompleteLine = (chargeLine, editable) =>
  chargeLine.some(line => {
    // Only validate rows that can be edited
    if (!(line.isNew || editable)) return false;

    return Object.keys(getLineErrors(line)).length > 0;
  });

const ChargeSection = ({
  section,
  onLineAdded = () => {},
  onLineEdited = () => {},
  onLineDeleted = () => {},
  onValidationChange = () => {},

  editable = false,
  orderDetails = {},
  groupGlByWarehouseSetting = false,
  glCodesList = [],
}) => {
  const [chargeLine, setChargeLine] = useState(section.lines || []);
  const invoiceType = section.key || "";

  useEffect(() => {
    setChargeLine(
      (section.lines || []).map(line => ({
        ...line,
        invoice_type: line.invoice_type || INVOICE_TYPE_MAP[section.key] || "",
        old_location_id: line.location_id,
        old_location_type: line.location_type,
      }))
    );
  }, [section.lines, section.key]);

  const showNoAutoCalc = NO_AUTO_CALC_SECTIONS.includes(section.key);
  const showDriverPay = DRIVER_PAY_SECTIONS.includes(section.key);
  const showExtraCheckboxCol = showNoAutoCalc || showDriverPay;

  const gridTemplateColumns = [
    "minmax(200px, 3.3fr)", // Description
    showExtraCheckboxCol ? "minmax(90px, 1.25fr)" : null,
    "minmax(90px, 0.85fr)", // Order Type
    "minmax(110px, 0.95fr)", // GL Code
    "minmax(60px, 0.75fr)", // Units
    "minmax(90px, 1.2fr)", // Amount
    editable ? "32px" : null, // Delete
  ]
    .filter(Boolean)
    .join(" ");

  const getLineKey = (line, idx) => (line.id ? line.id : `new-${idx}`);

  const updateLineField = (line, idx, field, value) => {
    const lineKey = getLineKey(line, idx);
    const updatedLine = { ...line, [field]: value };

    setChargeLine(prev => prev.map((l, i) => (getLineKey(l, i) === lineKey ? updatedLine : l)));

    if (updatedLine.isNew) {
      onLineAdded(section.key, lineKey, updatedLine);
    } else {
      onLineEdited(section.key, lineKey, updatedLine);
    }
  };

  const startAdd = () => {
    setChargeLine(prev => [...prev, newLineTemplate(section.key, orderDetails)]);
  };

  const handleDelete = (line, idx) => {
    const lineKey = getLineKey(line, idx);

    setChargeLine(prev => prev.filter((l, i) => getLineKey(l, i) !== lineKey));

    // If it was still an unconfirmed/untracked new row, wasNeverAdded=true
    // so the parent doesn't try to add a real "delete" entry for it.
    onLineDeleted(section.key, lineKey, line, !!line.isNew);
  };

  const renderChargeLine = (line, idx) => {
    const isNew = !!line.isNew;
    const lineKey = getLineKey(line, idx);
    const rowEditable = isNew || editable;
    const lineErrors = rowEditable ? getLineErrors(line) : {};

    return (
      <div className="charge-line" key={lineKey} style={{ gridTemplateColumns }}>
        <div className="charge-line__col charge-line__col--desc">
          {isNew ? (
            <>
              <Input
                size="small"
                placeholder="Description"
                value={line.invoice_name}
                onChange={e => updateLineField(line, idx, "invoice_name", e.target.value)}
                className={lineErrors.invoice_name ? "charge-line__input--error" : ""}
              />
              {lineErrors.invoice_name && (
                <div className="charge-line__field-error">{lineErrors.invoice_name}</div>
              )}
            </>
          ) : (
            <div className="charge-line__desc-title">
              {line.invoice_name}
              {line.added_from !== "AUTO" && (
                <sup style={{ color: "#f5222d", fontWeight: 600, fontSize: 11, marginLeft: 4 }}>
                  M
                </sup>
              )}
            </div>
          )}
        </div>

        {showExtraCheckboxCol && (
          <div className="charge-line__col charge-line__col--checkbox">
            {showNoAutoCalc &&
              !isNew &&
              line.added_from === "AUTO" &&
              (editable ? (
                <Checkbox
                  checked={!!line.is_modified}
                  onChange={e => updateLineField(line, idx, "is_modified", e.target.checked)}
                />
              ) : (
                <Checkbox checked={!!line.is_modified} disabled />
              ))}

            {showDriverPay &&
              (rowEditable ? (
                <Checkbox
                  checked={!!line.apply_driver_pay_for_adjustment}
                  onChange={e =>
                    updateLineField(line, idx, "apply_driver_pay_for_adjustment", e.target.checked)
                  }
                />
              ) : (
                <Checkbox checked={!!line.apply_driver_pay_for_adjustment} disabled />
              ))}
          </div>
        )}

        <div className="charge-line__col charge-line__col--type">
          {isNew ? (
            <>
              <Select
                size="small"
                // style={{ width: 120 }}
                style={{ width: "100%" }}
                value={line.location_id || undefined}
                className={lineErrors.location_id ? "charge-line__select--error" : ""}
                onChange={value => {
                  const loc = (orderDetails.all_locations || []).find(l => l.location_id === value);
                  setChargeLine(prev =>
                    prev.map((l, i) => {
                      if (getLineKey(l, i) !== lineKey) return l;
                      const updated = {
                        ...l,
                        location_id: value,
                        location_type: loc ? loc.order_type : l.location_type,
                      };
                      if (updated.isNew) {
                        onLineAdded(section.key, lineKey, updated);
                      } else {
                        onLineEdited(section.key, lineKey, updated);
                      }
                      return updated;
                    })
                  );
                }}
                placeholder="Order Type"
              >
                {(orderDetails.all_locations || []).map(loc => (
                  <Select.Option key={loc.location_id} value={loc.location_id}>
                    {loc.order_type}
                  </Select.Option>
                ))}
              </Select>
              {lineErrors.location_id && (
                <div className="charge-line__field-error">{lineErrors.location_id}</div>
              )}
            </>
          ) : (
            (line.order_type || line.location_type) && (
              <Tag
                className={`charge-line__type-tag charge-line__type-tag--${(
                  line.order_type || line.location_type
                ).toLowerCase()}`}
              >
                {line.order_type || line.location_type}
              </Tag>
            )
          )}
        </div>

        <div className="charge-line__col charge-line__col--gl">
          {editable ? (
            <>
              <GLCodeSelect
                invoiceType={invoiceType}
                defaultValue={!groupGlByWarehouseSetting ? line?.gl_code : ""}
                data={glCodesList || []}
                value={line.gl_code}
                onChange={value => updateLineField(line, idx, "gl_code", value)}
                selectKey={"gl_code"}
                selectValue={"gl_code"}
                selectedValue={"gl_code"}
                selectedCode={"description"}
                style={{ width: "100%" }}
                showSearch={true}
                placeholder={I18n.t("invoices.gl_code")}
                dropdownMatchSelectWidth={false}
                size="small"
                toDisable={false}
                className={lineErrors.gl_code ? "charge-line__select--error" : ""}
              />
              {lineErrors.gl_code && (
                <div className="charge-line__field-error">{lineErrors.gl_code}</div>
              )}
            </>
          ) : (
            line.gl_code || "-"
          )}
        </div>

        <div className="charge-line__col charge-line__col--units">
          {rowEditable ? (
            <>
              <Input
                size="small"
                type="number"
                value={line.invoice_quantity}
                onChange={e => updateLineField(line, idx, "invoice_quantity", e.target.value)}
                className={lineErrors.invoice_quantity ? "charge-line__input--error" : ""}
              />
              {lineErrors.invoice_quantity && (
                <div className="charge-line__field-error">{lineErrors.invoice_quantity}</div>
              )}
            </>
          ) : (
            <span className="charge-line__text-value">{line.invoice_quantity}</span>
          )}
        </div>

        <div className="charge-line__col charge-line__col--amount">
          {rowEditable ? (
            <>
              <Input
                size="small"
                type="number"
                prefix="$"
                value={line.invoice_value}
                onChange={e => updateLineField(line, idx, "invoice_value", e.target.value)}
                className={lineErrors.invoice_value ? "charge-line__input--error" : ""}
              />
              {lineErrors.invoice_value && (
                <div className="charge-line__field-error">{lineErrors.invoice_value}</div>
              )}
            </>
          ) : (
            <span className="charge-line__text-value">
              ${Number(line.invoice_value || 0).toFixed(2)}
            </span>
          )}
        </div>

        {editable && (
          <div className="charge-line__col charge-line__col--actions">
            <Tooltip title="Remove">
              <Icon
                type="delete"
                className="charge-line__action-icon charge-line__action-icon--danger"
                onClick={() => handleDelete(line, idx)}
              />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  const hasEmptyFields = hasIncompleteLine(chargeLine, editable);

  useEffect(() => {
    onValidationChange(section.key, hasEmptyFields);
    // eslint-disable-next-line
  }, [section.key, chargeLine, editable]);

  return (
    <div className="charge-section">
      <div className="charge-section__header">
        <span className="charge-section__title">{section.title}</span>
        <span className="charge-section__lines-badge">
          {chargeLine.length} {chargeLine.length === 1 ? "line" : "lines"}
        </span>
        <span className="charge-section__subtotal">Subtotal ${section.subtotal.toFixed(2)}</span>
      </div>

      <div
        className={`charge-section__table${editable ? "" : " charge-section__table--view-only"}`}
      >
        <div className="charge-line charge-line--head" style={{ gridTemplateColumns }}>
          <div className="charge-line__col charge-line__col--desc"># DESCRIPTION</div>
          {showExtraCheckboxCol && (
            <div className="charge-line__col charge-line__col--checkbox">
              {showNoAutoCalc ? "NO AUTO CALC" : "ELIGIBLE FOR DRIVER PAY"}
            </div>
          )}
          <div className="charge-line__col charge-line__col--type">ORDER TYPE</div>
          <div className="charge-line__col charge-line__col--gl">GL CODE</div>
          <div className="charge-line__col charge-line__col--units">UNITS</div>
          <div className="charge-line__col charge-line__col--amount">AMOUNT</div>
          {editable && <div className="charge-line__col charge-line__col--actions" />}
        </div>
        {chargeLine.map(renderChargeLine)}
      </div>

      {editable && (
        <>
          <Button
            className="charge-section__add-btn"
            icon="plus"
            onClick={startAdd}
            disabled={hasEmptyFields}
          >
            Add {section.title.replace(/S$/, "")} Line
          </Button>
          {hasEmptyFields && (
            <div className="charge-section__error-text">
              Please fill in all required fields before adding a new line or saving.
            </div>
          )}
        </>
      )}
    </div>
  );
};

ChargeSection.propTypes = {
  section: PropTypes.shape({
    key: PropTypes.string,
    title: PropTypes.string,
    subtotal: PropTypes.number,
    lines: PropTypes.array,
  }).isRequired,
  onLineAdded: PropTypes.func,
  onLineEdited: PropTypes.func,
  onLineDeleted: PropTypes.func,
  editable: PropTypes.bool,
  glCodesList: PropTypes.array,
  orderDetails: PropTypes.object,
  onValidationChange: PropTypes.func,
};

export default ChargeSection;
